import { Controller } from "@hotwired/stimulus"

// Kontroler stworzony z myślą o izolacji struktury plików poza kontenerem
// np. nieznana zawartość emaila nie będzie korzystać / koegzystować ze strukturą aplikacji poza iframe

// Z założenia miejsce w kórym pojawia się iframe powinno być opakowane przez inny div
// tak, że iframe ma nieograniczoną szerokość i wysokość, a zewnętrzny div wtedy tworzy scrollbara
// przykład stylu iframe'a: .email_iframe w emails.css

// Zachowanie względem lazy w iframe: (_lazy_ - czy iframe ma być ładowany leniwie)
// - jeśli _lazy_ może być true, to potrzebujemy _src_ do renderowania
// - jeśli _lazy_ może być false, to można dodać _preload_ i bezpośrednio wstawić iframe do DOM (pomijamy żądanie http)
// - gdy _lazy_ jest zawsze true, to nie ma sensu dodawać _preload_
// - gdy _lazy_ jest zawsze false, to można się ograniczyć tylko do _preload_ i wtedy nie ma potrzeby dodawania _src_

// Zachowanie względem mailPreview (podgląd wiadomości mający wyglądać jak u odbiorcy, np. karta
// kampanii marketingowej). Domyślnie false = zachowanie z inboxa. True zmienia cztery rzeczy:
// - dokument bez arkuszy aplikacji (reset Tailwinda nadpisywałby style wiadomości i podgląd kłamałby)
// - iframe zostaje na szerokości kontenera, a za szeroka treść przewija się wewnątrz ramki
//   (bez tego iframe rozpycha się do naturalnej szerokości maila i lewo-prawo jeździ cała karta)
// - sandbox bez allow-scripts - treść wiadomości jest sanityzowana, ale blob URL działa w originie
//   aplikacji, więc wyłączony JS to druga warstwa zabezpieczenia
// - linki otwierane w nowej karcie, żeby klik w wiadomość nie zamieniał podglądu na stronę docelową

export default class extends Controller {
  static values = { src: String, className: String, lazy: Boolean, mailPreview: Boolean }
  static targets = ["preloadContent"]

  connect() {
    console.log("iframe controller connect")

    if (document.hidden && this.lazyValue) {
      // Gdy strona ładowana jest w dodatkowej karcie to frame z lazy jest pomijany
      this.addRenderAfterVisibilityChangeListener()
    } else {
      this.renderIframe()
    }
  }

  // usuwamy iframe, aby po powrocie do strony nie mieć starych i pustych iframe'ów
  disconnect() {
    const iframe = this.element.querySelector("iframe")
    if (iframe) {
      if (this.resizeObserver) this.resizeObserver.disconnect()
      iframe.remove()
    }
  }

  renderIframe() {
    const iframe = document.createElement("iframe")
    iframe.className = this.classNameValue
    iframe.style.visibility = "hidden"

    if (this.lazyValue) {
      iframe.setAttribute("loading", "lazy")
    }
    if (this.mailPreviewValue) {
      // allow-same-origin jest konieczne, żeby rodzic czytał `contentDocument` przy auto-resize;
      // brak allow-scripts wyłącza JS w treści, allow-popups przepuszcza linki z target="_blank"
      iframe.setAttribute("sandbox", "allow-same-origin allow-popups allow-popups-to-escape-sandbox")
    } else {
      // poza podglądem wiadomości iframe nigdy nie przewija - dopasowuje się do treści
      iframe.setAttribute("scrolling", "no")
    }

    if (!this.lazyValue && this.hasPreloadContentTarget) {
      // korzystamy z preloadContent
      const preloadedContent = this.preloadedContentFromTarget()
      const blob = new Blob([preloadedContent], { type: "text/html" })
      const url = URL.createObjectURL(blob)
      iframe.onload = () => {
        URL.revokeObjectURL(url) // zwalniamy pamięć
        if (!this.resizeObserver) this.addAutoResizeListener(iframe) // fallback gdyby setupEarlyVisibility nie zadziałał
        this.resizeIframe(iframe) // finalny resize po załadowaniu wszystkich zasobów
      }
      iframe.src = url
    } else {
      // jest lazy lub nie ma preloadContent
      iframe.onload = () => {
        if (!this.resizeObserver) this.addAutoResizeListener(iframe) // fallback
        this.resizeIframe(iframe) // finalny resize po załadowaniu wszystkich zasobów
      }
      iframe.src = this.srcValue
    }

    this.element.appendChild(iframe)
    this.setupEarlyVisibility(iframe)
  }

  // Nie czekamy na załadowanie obrazków — pokazujemy iframe gdy DOM jest gotowy (readyState !== "loading")
  setupEarlyVisibility(iframe) {
    const check = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        // pomijamy about:blank — to wstępny dokument przed załadowaniem właściwej treści (lazy/HTTP path)
        if (doc && doc.readyState !== "loading" && !doc.URL.startsWith("about:")) {
          this.addAutoResizeListener(iframe)
          return
        }
      } catch (_e) { return } // cross-origin — onload fallback obsłuży
      requestAnimationFrame(check)
    }
    requestAnimationFrame(check)
  }

  // dopasowuje rozmiar iframe do zawartości, nie robimy tego poza iframe, przez wpływ elementów zewnętrznych
  // NOTE: - gdyby się okazało, że źle wylicza wielkość, może być kwestia, że nie zdąża uwględnić styli i obrazków
  //         wówczas warto rozważyć, dodanie nasłuchiwania na ich ładowanie wewnątrz iframe
  resizeIframe(iframe) {
    // guard — iframe może być już usunięty lub jeszcze nie gotowy
    if (!iframe.contentWindow?.document?.body) return

    // wymuszenie przeliczenia szerokości elementów na postawie wielkości kontenera
    // zapewnia to, dostosowanie automatycznych szerokości elementów wewnątrz iframe, do nowych warunków
    iframe.style.width = this.element.clientWidth + "px"

    // przeliczenie wymiarów na podstawie wygenerowanej zawartości
    // height: dodane no scrolling i bez documentElement - możliwe, że tak lepiej - aby zapobiec minium 150 px wysokości na przeglądarkach
    const doc = iframe.contentWindow.document
    let height = doc.body.scrollHeight
    const width = Math.max(doc.body.scrollWidth, doc.documentElement.scrollWidth)

    // mailPreview - szerokości nie ruszamy (iframe ma zostać w kontenerze), doliczamy tylko wysokość
    // poziomego paska, gdy treść nie mieści się w ramce - inaczej pasek przyciąłby ostatnią linię
    if (this.mailPreviewValue) {
      if (width > iframe.clientWidth) { height += this.horizontalScrollbarHeight(iframe, height) }
      if (iframe.style.height !== height + "px") { iframe.style.height = height + "px" }
      iframe.style.visibility = "visible"
      return
    }

    if (iframe.style.height !== height + "px") { iframe.style.height = height + "px" }
    if (iframe.style.width !== width + "px") { iframe.style.width = width + "px" }
    iframe.style.visibility = "visible"
    // console.log(`iframe resized to ${width}x${height} ${this.isLoaded(iframe) ? "loaded" : "not loaded"}`)
  }

  // Wysokość poziomego paska przewijania wewnątrz iframe. Mierzymy realnie, bo zależy od systemu
  // i przeglądarki (od nakładkowego paska o zerowej wysokości do ~17 px). Wymaga ustawienia
  // docelowej wysokości ramki - dopiero wtedy `documentElement.clientHeight` (widoczny obszar
  // bez paska) różni się od wysokości iframe'a o grubość paska.
  horizontalScrollbarHeight(iframe, contentHeight) {
    iframe.style.height = contentHeight + "px"

    const viewportHeight = iframe.contentWindow.document.documentElement.clientHeight
    return Math.max(0, iframe.clientHeight - viewportHeight)
  }

  preloadedContentFromTarget() {
    const content = this.preloadContentTarget.innerHTML
    // podgląd wiadomości bez arkuszy aplikacji - ma wyglądać jak u odbiorcy, a nie jak strona
    const stylesheets = this.mailPreviewValue
      ? ""
      : Array.from(document.querySelectorAll("link[rel=\"stylesheet\"]"))
        .map(link => `<link rel="stylesheet" href="${link.href}">`)
        .join("\n")
    // target="_blank" tylko w podglądzie wiadomości - bez tego klik w link maila zamienia podgląd
    // na stronę docelową (albo na 404, bo link wypisu ma token "preview")
    const baseTarget = this.mailPreviewValue ? " target=\"_blank\"" : ""
    const baseHref = `<base href="${window.location.origin}"${baseTarget}>`

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          ${baseHref}
          ${stylesheets}
        </head>
        <body>
          ${content}
        </body>
      </html>
    `
  }

  // reakcja na natywne zdarzene visibilitychange przeglądarki
  addRenderAfterVisibilityChangeListener() {
    this.visibilityHandler = () => {
      if (!document.hidden) {
        this.renderIframe()
        document.removeEventListener("visibilitychange", this.visibilityHandler)
      }
    }
    document.addEventListener("visibilitychange", this.visibilityHandler)
  }

  // reakcja na zmianę wielkości elementu - aktualizacja rozmiaru iframe
  // głównie przydaje się w kwestii wysokości, jak pojawia się element na widoku (z niewidzocznego obszaru)
  addAutoResizeListener(iframe) {
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.resizeIframe(iframe)
      })
    })
    this.resizeObserver.observe(this.element)

    // po załadowaniu każdego obrazka, przelicz rozmiar iframe
    // (obrazki mogą się ładować po ustawieniu visibility, zmieniając wysokość contentu)
    this.addImageLoadListeners(iframe)
  }

  // nasłuchiwanie na załadowanie obrazków wewnątrz iframe, po każdym przelicza rozmiar
  addImageLoadListeners(iframe) {
    try {
      const imgs = iframe.contentDocument?.querySelectorAll("img")
      if (!imgs) return

      imgs.forEach(img => {
        if (!img.complete) {
          img.addEventListener("load", () => this.resizeIframe(iframe), { once: true })
        }
      })
    } catch (_e) { /* cross-origin — pomijamy */ }
  }

  // może rzucić wyjątek - CORS - ale my używamy tylko naszej domeny, więc ok
  isLoaded(iframe) {
    return iframe && iframe.contentWindow && iframe.contentWindow.document.readyState === "complete"
  }
}
